import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { ChangeDiff, SUPPLIER_LABELS, CUSTOMER_LABELS, BOM_LABELS } from "@/components/ChangeDiff";
import { SupplierDialog } from "@/components/purchasing/SuppliersPanel";
import { CustomerDialog } from "@/components/customers/CustomersPanel";
import { ResubmitDialog } from "@/components/purchasing/PurchaseOrdersPanel";
import BomFormModal from "@/components/bom/BomFormModal";
import { Inbox, ShoppingCart, Boxes, Users2, Contact, RotateCcw, Loader2, CheckCircle2, ExternalLink } from "lucide-react";

const KIND_META = {
  po: { label: "Purchase Order", tab: "purchasing", Icon: ShoppingCart, chip: "bg-blue-100 text-blue-700" },
  bom: { label: "BOM Item", tab: "bom", Icon: Boxes, chip: "bg-indigo-100 text-indigo-700" },
  supplier: { label: "Supplier", tab: "suppliers", Icon: Users2, chip: "bg-teal-100 text-teal-700" },
  customer: { label: "Customer", tab: "customers", Icon: Contact, chip: "bg-amber-100 text-amber-700" },
};

const RECENT_WIN_MS = 48 * 3600 * 1000; // "recently approved" window
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

function Row({ e, children }) {
  const meta = KIND_META[e.kind];
  const Icon = meta.Icon;
  return (
    <div className="px-5 py-3 flex items-start gap-3" data-testid={`my-appr-row-${e.kind}-${e.id}`}>
      <span className={`mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded ${meta.chip}`}><Icon className="h-3 w-3" />{meta.label}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-800">{e.title}</p>
        <p className="text-[11px] text-slate-400 truncate">{e.subtitle}</p>
        {children}
        {e.changes && <div className="max-w-md"><ChangeDiff changes={e.changes} labels={e.labels} testidPrefix={`my-appr-${e.kind}-${e.id}`} /></div>}
      </div>
    </div>
  );
}

// Planner-facing "My Approvals": my pending requests, rejected items with Fix & Resubmit,
// and everything an admin signed off in the last 48 hours.
export default function MyApprovals({ currentUsername, facility, bomItems = [], suppliers = [], customers = [], machines = [], poVersion = 0, onResubmitSupplier, onResubmitCustomer, onResubmitPO, onSaveBomItem, onNavigate }) {
  const [myPos, setMyPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(null); // { kind, raw } — rejected record being fixed

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLoading(true);
    api.get(`/purchase-orders?facility=${encodeURIComponent(facility)}&mine=true`)
      .then((d) => setMyPos(Array.isArray(d) ? d : d.items || []))
      .catch((e) => { logger.error("My Approvals: PO load failed:", e); setMyPos([]); })
      .finally(() => setLoading(false));
  }, [facility, poVersion]);

  const entries = useMemo(() => {
    const mine = (r) => r.created_by === currentUsername || r.updated_by === currentUsername || r.resubmitted_by === currentUsername;
    const rows = [];
    myPos.forEach((p) => rows.push({ kind: "po", id: p.id, title: p.po_number, subtitle: `${p.supplier || "—"} · ${(p.items || []).map((i) => `${i.name} ×${i.ordered_qty}`).join(", ")}`, status: p.status, reason: p.rejection_note, at: p.created_at, approvedAt: p.approved_at, approvedBy: p.approved_by, raw: p }));
    bomItems.filter(mine).forEach((b) => rows.push({ kind: "bom", id: b.id, title: b.name, subtitle: `${b.sku} · ${b.item_type}`, status: b.approval_status || "Active", reason: b.rejection_reason, at: b.updated_at || b.created_at, approvedAt: b.approved_at, approvedBy: b.approved_by, changes: b.pending_changes, labels: BOM_LABELS, raw: b }));
    suppliers.filter(mine).forEach((s) => rows.push({ kind: "supplier", id: s.id, title: s.name, subtitle: [s.code, s.phone].filter(Boolean).join(" · "), status: s.status, reason: s.rejection_reason, at: s.created_at, approvedAt: s.approved_at, approvedBy: s.approved_by, changes: s.pending_changes, labels: SUPPLIER_LABELS, raw: s }));
    customers.filter(mine).forEach((c) => rows.push({ kind: "customer", id: c.id, title: c.name, subtitle: [c.code, c.phone].filter(Boolean).join(" · "), status: c.status, reason: c.rejection_reason, at: c.created_at, approvedAt: c.approved_at, approvedBy: c.approved_by, changes: c.pending_changes, labels: CUSTOMER_LABELS, raw: c }));
    return rows;
  }, [myPos, bomItems, suppliers, customers, currentUsername]);

  const byAtDesc = (a, b) => new Date(b.at || 0) - new Date(a.at || 0);
  const pending = entries.filter((e) => e.status === "Pending Approval").sort(byAtDesc);
  const rejected = entries.filter((e) => e.status === "Rejected").sort(byAtDesc);
  const wins = entries
    .filter((e) => e.approvedAt && e.status !== "Pending Approval" && e.status !== "Rejected" && Date.now() - new Date(e.approvedAt).getTime() < RECENT_WIN_MS)
    .sort((a, b) => new Date(b.approvedAt) - new Date(a.approvedAt));

  return (
    <div className="space-y-6" data-testid="my-approvals">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4 flex items-center gap-2">
        <Inbox className="h-5 w-5 text-purple-600" />
        <div>
          <h3 className="font-display text-base font-semibold text-slate-900">My Approvals</h3>
          <p className="text-xs text-slate-400">Your requests waiting for admin sign-off, items sent back for fixes, and recent approvals.</p>
        </div>
        <span className="ml-auto text-xs text-slate-400" data-testid="my-appr-counts">{pending.length} pending · {rejected.length} rejected · {wins.length} approved recently</span>
      </div>

      {/* Rejected — needs fixing */}
      <div className="bg-white rounded-xl border border-rose-200 shadow-sm" data-testid="my-appr-rejected-section">
        <p className="px-5 py-3 border-b border-rose-100 text-sm font-semibold text-rose-700">Needs Fixing · Rejected ({rejected.length})</p>
        {rejected.length === 0 ? (
          <p className="px-5 py-6 text-center text-xs text-slate-400 italic" data-testid="my-appr-rejected-empty">Nothing rejected right now.</p>
        ) : (
          <div className="divide-y divide-rose-50">
            {rejected.map((e) => (
              <Row key={`${e.kind}-${e.id}`} e={e}>
                {e.reason && <p className="text-[11px] text-rose-600 font-medium mt-0.5" data-testid={`my-appr-reason-${e.kind}-${e.id}`}>Rejected: {e.reason}</p>}
                <div className="mt-1.5 flex items-center gap-2">
                  <Button size="sm" className="h-7 text-[11px] bg-purple-600 hover:bg-purple-700" onClick={() => setFixing(e)} data-testid={`my-appr-fix-${e.kind}-${e.id}`}>
                    <RotateCcw className="h-3 w-3 mr-1" /> Fix &amp; Resubmit
                  </Button>
                  {onNavigate && (
                    <button onClick={() => onNavigate(KIND_META[e.kind].tab)} className="text-[11px] text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-0.5" data-testid={`my-appr-open-${e.kind}-${e.id}`}>
                      <ExternalLink className="h-3 w-3" /> Open in tab
                    </button>
                  )}
                </div>
              </Row>
            ))}
          </div>
        )}
      </div>

      {/* Pending Approval */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="my-appr-pending-section">
        <p className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Pending Approval ({pending.length})</p>
        {loading ? (
          <p className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm" data-testid="my-appr-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
        ) : pending.length === 0 ? (
          <p className="px-5 py-6 text-center text-xs text-slate-400 italic" data-testid="my-appr-pending-empty">No requests waiting for approval.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {pending.map((e) => (
              <Row key={`${e.kind}-${e.id}`} e={e}>
                <p className="text-[10px] text-purple-500 mt-0.5">Awaiting admin approval · submitted {fmtWhen(e.at)}</p>
              </Row>
            ))}
          </div>
        )}
      </div>

      {/* Recently Approved (48h) */}
      <div className="bg-white rounded-xl border border-emerald-200 shadow-sm" data-testid="my-appr-wins-section">
        <p className="px-5 py-3 border-b border-emerald-100 text-sm font-semibold text-emerald-700">Recently Approved · Last 48 Hours ({wins.length})</p>
        {wins.length === 0 ? (
          <p className="px-5 py-6 text-center text-xs text-slate-400 italic" data-testid="my-appr-wins-empty">Nothing approved in the last 48 hours.</p>
        ) : (
          <div className="divide-y divide-emerald-50">
            {wins.map((e) => (
              <Row key={`${e.kind}-${e.id}`} e={e}>
                <p className="text-[11px] text-emerald-600 font-medium mt-0.5 inline-flex items-center gap-1" data-testid={`my-appr-win-${e.kind}-${e.id}`}>
                  <CheckCircle2 className="h-3 w-3" /> Approved{e.approvedBy ? ` by ${e.approvedBy}` : ""} · {fmtWhen(e.approvedAt)}
                </p>
              </Row>
            ))}
          </div>
        )}
      </div>

      {fixing?.kind === "supplier" && (
        <SupplierDialog supplier={fixing.raw} resubmit onClose={() => setFixing(null)} onSave={(existing, payload) => onResubmitSupplier(existing, payload)} />
      )}
      {fixing?.kind === "customer" && (
        <CustomerDialog customer={fixing.raw} resubmit onClose={() => setFixing(null)} onSave={(existing, payload) => onResubmitCustomer(existing, payload)} />
      )}
      {fixing?.kind === "po" && (
        <ResubmitDialog po={fixing.raw} onClose={() => setFixing(null)} onResubmit={onResubmitPO} />
      )}
      {fixing?.kind === "bom" && (
        <BomFormModal open item={fixing.raw} items={bomItems} suppliers={suppliers} machines={machines}
          onOpenChange={(v) => !v && setFixing(null)} onSubmit={(payload) => onSaveBomItem(payload, fixing.raw)} />
      )}
    </div>
  );
}
